import { useEffect, useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { api } from "../../../api";
import { toast } from "sonner";
import { Mic, Square, Sparkles, Loader2, MessageSquare, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientDetailsLabels } from "../labels";

interface NoteModalProps {
  clientId: number | string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newEvent: any) => void;
}

async function accelerateAudio(audioBlob: Blob, speedFactor: number = 1.3): Promise<Blob> {
  try {
    const audioContext = new AudioContext();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const outputContext = new AudioContext();
    const source = outputContext.createBufferSource();
    const destination = outputContext.createMediaStreamDestination();
    source.buffer = audioBuffer;
    source.connect(destination);
    source.playbackRate.value = speedFactor;
    const mediaRecorder = new MediaRecorder(destination.stream, {
      mimeType: audioBlob.type || "audio/webm;codecs=opus",
    });
    const chunks: Blob[] = [];
    return new Promise((resolve, reject) => {
      mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) chunks.push(event.data); };
      mediaRecorder.onstop = () => { resolve(new Blob(chunks, { type: audioBlob.type })); };
      mediaRecorder.onerror = reject;
      mediaRecorder.start();
      source.start(0);
      setTimeout(() => {
        source.stop();
        mediaRecorder.stop();
      }, (audioBuffer.duration * 1000) / speedFactor);
    });
  } catch (error) {
    console.error("Erro ao acelerar áudio:", error);
    return audioBlob;
  }
}

async function convertAudioToText(audioBlob: Blob): Promise<string> {
  try {
    const acceleratedAudio = await accelerateAudio(audioBlob, 1.3);
    let extension = "webm";
    if (acceleratedAudio.type.includes("mp4")) extension = "mp4";
    else if (acceleratedAudio.type.includes("ogg")) extension = "ogg";
    else if (acceleratedAudio.type.includes("wav")) extension = "wav";
    
    const formData = new FormData();
    formData.append("file", acceleratedAudio, `audio_accelerated.${extension}`);
    
    const response = await fetch("/api/ai/transcribe", {
      method: "POST",
      body: formData,
    });
    
    if (!response.ok) throw new Error("Erro na transcrição");
    const result = await response.json();
    return result.text;
  } catch (error) {
    console.error("Erro ao converter áudio:", error);
    throw error;
  }
}

export function NoteModal({ clientId, isOpen, onClose, onSuccess }: NoteModalProps) {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].noteModal;
  
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [loading, setLoading] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setText("");
      setIsRecording(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (recordingIntervalRef.current) {
        window.clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      setRecordingTime(0);
    }
    return () => {
      if (recordingIntervalRef.current) window.clearInterval(recordingIntervalRef.current);
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const supportedTypes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      let mimeType = "audio/webm;codecs=opus";
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) { mimeType = type; break; }
      }
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) audioChunksRef.current.push(event.data); };
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setIsConverting(true);
        try {
          const transcribedText = await convertAudioToText(audioBlob);
          setText((prev) => prev + (prev ? " " : "") + transcribedText);
          toast.success(t.toasts.audioConverted);
        } catch (error) {
          toast.error(t.toasts.audioError);
        } finally {
          setIsConverting(false);
        }
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      toast.error("Erro ao acessar microfone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
    }
  };

  const improveText = async () => {
    if (!text.trim()) return;
    setIsImproving(true);
    try {
      const res = await fetch("/api/ai/improve", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("Erro na IA");
      const json = await res.json();
      if (json.text) setText(json.text);
    } catch (e) {
      toast.error(t.toasts.aiError);
    } finally {
      setIsImproving(false);
    }
  };

  const handleSave = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await api.createTimelineEvent(clientId, { 
        content: text,
        event_type: 'note'
      });
      if (res.ok) {
        toast.success(t.toasts.success);
        if (onSuccess) onSuccess(res.data);
        onClose();
      } else {
        toast.error(t.toasts.error);
      }
    } catch (err) {
      toast.error(t.toasts.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-y-auto max-h-[95vh] border-0 rounded-3xl shadow-2xl bg-white [&>button]:hidden">
        <DialogHeader className="p-6 border-b border-neutral-50 bg-neutral-50/30 flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
              <MessageSquare className="w-5 h-5" />
            </div>
            <DialogTitle className="text-lg font-bold text-neutral-800 tracking-tight">{t.title}</DialogTitle>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-xl hover:bg-neutral-100">
            <X className="w-5 h-5 text-neutral-400" />
          </Button>
        </DialogHeader>

        <div className="p-6 space-y-6">
          <div className={cn(
            "border rounded-2xl p-5 flex items-center justify-between gap-4 transition-all",
            isRecording ? "bg-red-50/50 border-red-100" : "bg-neutral-50/50 border-neutral-100"
          )}>
            {!isRecording ? (
              <>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-white border border-neutral-100 flex items-center justify-center text-neutral-400 shadow-sm">
                    <Mic className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-neutral-800">{t.audio.title}</p>
                    <p className="text-[11px] text-neutral-400 font-medium">{t.audio.subtitle}</p>
                  </div>
                </div>
                <Button 
                  onClick={startRecording} 
                  disabled={isConverting}
                  variant="outline"
                  className="h-10 px-6 font-bold rounded-xl border-neutral-200 hover:bg-white transition-all active:scale-95"
                >
                  {isConverting ? <Loader2 className="animate-spin h-4 w-4" /> : t.audio.buttonStart}
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-red-500 flex items-center justify-center text-white animate-pulse shadow-lg shadow-red-200">
                    <Mic className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm font-bold text-red-600">{t.audio.recording}</p>
                    <span className="text-xs font-mono font-bold text-red-400">
                      {Math.floor(recordingTime / 60).toString().padStart(2, "0")}:
                      {(recordingTime % 60).toString().padStart(2, "0")}
                    </span>
                  </div>
                </div>
                <Button 
                  onClick={stopRecording} 
                  variant="destructive" 
                  className="h-10 px-6 font-bold rounded-xl gap-2 transition-all active:scale-95"
                >
                  <Square className="h-3 w-3 fill-current" />
                  {t.audio.buttonStop}
                </Button>
              </>
            )}
          </div>

          {isConverting && (
            <div className="flex items-center justify-center gap-3 p-4 bg-blue-50 text-blue-700 rounded-2xl text-[11px] font-bold border border-blue-100 animate-pulse uppercase tracking-widest">
              <Loader2 className="animate-spin h-3.5 w-3.5" />
              {t.audio.converting}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest pl-1">{t.content.label}</Label>
              <Button
                type="button"
                onClick={improveText}
                disabled={isImproving || !text.trim()}
                variant="ghost"
                size="sm"
                className="h-8 px-3 gap-2 text-emerald-600 hover:text-white hover:bg-emerald-600 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all"
              >
                {isImproving ? <Loader2 className="animate-spin h-3 w-3" /> : <Sparkles className="h-3.5 w-3.5" />}
                {isImproving ? t.content.improving : t.content.improve}
              </Button>
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={t.content.placeholder}
              className="resize-none focus-visible:ring-emerald-500/20 border-neutral-100 rounded-2xl min-h-[200px] text-sm leading-relaxed p-4"
            />
          </div>
        </div>

        <div className="p-6 bg-neutral-50/50 border-t border-neutral-50 flex gap-3">
          <Button variant="ghost" onClick={onClose} className="flex-1 h-12 font-bold text-neutral-400 rounded-2xl hover:bg-white hover:text-neutral-600 transition-all">
            {t.actions.cancel}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading || isRecording || isConverting || text.trim().length < 2} 
            className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all active:scale-[0.98] disabled:bg-neutral-100 disabled:text-neutral-400"
          >
            {loading ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : null}
            {t.actions.save}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function cn(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}
